import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import { formatDateInTimezone } from '../utils/timezone';

interface CancellationWindow {
  units: 'weeks' | 'days' | 'hours' | 'minutes';
  quantity: number;
}

interface Rental {
  _id: string;
  listingId: { 
    _id: string; 
    name: string;
    title: string;
    timezone: string;
    cancellationWindow: CancellationWindow;
  };
  windowId: { _id: string; startDateTime: Date; endDateTime: Date };
  status: string;
  totalPrice: number;
  startDateTime?: Date;
  displayEndDateTime?: Date;
  price?: number;
  contactAddress?: string;
  contactPhone?: string;
  cancellationReason?: string;
  cancelledBy?: 'owner' | 'renter' | 'admin';
  cancelledAt?: string;
}

const MyRentalsPage: React.FC = () => {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRentals();
  }, []);

  const fetchRentals = async () => {
    try {
      const data = await api.get<{ rentals: Rental[] }>('/rentals/my-rentals');
      setRentals(data.rentals);
    } catch {
      toast.error('Failed to load rentals');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if cancellation is allowed based on cancellation window
  const isCancellationAllowed = (rental: Rental): { allowed: boolean; reason?: string } => {
    if (rental.status !== 'confirmed') {
      return { allowed: false, reason: 'Rental is not confirmed' };
    }

    const startTime = new Date(rental.startDateTime || rental.windowId.startDateTime);
    const now = new Date();
    const window = rental.listingId.cancellationWindow;
    
    if (!window) {
      return { allowed: true };
    }

    // Calculate time difference in milliseconds
    const diffMs = startTime.getTime() - now.getTime();
    
    // Convert cancellation window to milliseconds
    let windowMs = 0;
    switch (window.units) {
      case 'weeks':
        windowMs = window.quantity * 7 * 24 * 60 * 60 * 1000;
        break;
      case 'days':
        windowMs = window.quantity * 24 * 60 * 60 * 1000;
        break;
      case 'hours':
        windowMs = window.quantity * 60 * 60 * 1000;
        break;
      case 'minutes':
        windowMs = window.quantity * 60 * 1000;
        break;
    }

    if (diffMs < windowMs) {
      const timeUnit = window.units.slice(0, -1); // Remove 's' from units
      return { 
        allowed: false, 
        reason: `Must cancel at least ${window.quantity} ${timeUnit}${window.quantity > 1 ? 's' : ''} before rental start` 
      };
    }

    return { allowed: true };
  };

  // Group rentals by listing
  const groupedRentals = rentals.reduce((acc, rental) => {
    const listingId = rental.listingId._id;
    if (!acc[listingId]) {
      acc[listingId] = {
        listing: rental.listingId,
        rentals: []
      };
    }
    acc[listingId].rentals.push(rental);
    return acc;
  }, {} as Record<string, { listing: Rental['listingId']; rentals: Rental[] }>);

  const handleCancel = async (rentalId: string) => {
    if (!window.confirm('Are you sure you want to cancel this rental?')) {
      return;
    }

    try {
      await api.post(`/rentals/${rentalId}/cancel/renter`);
      toast.success('Rental cancelled successfully');
      fetchRentals();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to cancel rental');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">My Rentals</h1>

      {rentals.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600">You don't have any rentals yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.values(groupedRentals).map((group) => (
            <div key={group.listing._id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Listing Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
                <h2 className="text-lg font-semibold text-gray-900">{group.listing.title}</h2>
              </div>

              {/* Rentals for this listing */}
              <div className="divide-y divide-gray-200">
                {group.rentals.map((rental) => {
                  const cancellationCheck = isCancellationAllowed(rental);
                  
                  return (
                    <div key={rental._id} className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="space-y-1 text-gray-600">
                            <p>
                              <span className="font-medium">Start:</span>{' '}
                              {formatDateInTimezone(
                                new Date(rental.startDateTime || rental.windowId.startDateTime),
                                rental.listingId.timezone
                              )}
                            </p>
                            <p>
                              <span className="font-medium">End:</span>{' '}
                              {formatDateInTimezone(
                                new Date(rental.displayEndDateTime || rental.windowId.endDateTime),
                                rental.listingId.timezone
                              )}
                            </p>
                            <p>
                              <span className="font-medium">Price:</span> ${(rental.price || rental.totalPrice).toFixed(2)}
                            </p>
                            <p>
                              <span className="font-medium">Status:</span>{' '}
                              <span
                                className={`font-semibold ${
                                  rental.status === 'confirmed'
                                    ? 'text-green-600'
                                    : rental.status === 'cancelled'
                                    ? 'text-red-600'
                                    : rental.status === 'completed'
                                    ? 'text-blue-600'
                                    : 'text-yellow-600'
                                }`}
                              >
                                {rental.status.charAt(0).toUpperCase() + rental.status.slice(1)}
                              </span>
                            </p>
                            {rental.contactAddress && (
                              <p>
                                <span className="font-medium">Address:</span>{' '}
                                <a 
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rental.contactAddress)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-700 underline"
                                >
                                  {rental.contactAddress}
                                </a>
                              </p>
                            )}
                            {rental.contactPhone && (
                              <p>
                                <span className="font-medium">Phone:</span> {rental.contactPhone}
                              </p>
                            )}
                          </div>
                          {rental.status === 'cancelled' && rental.cancellationReason && (
                            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-red-800 mb-1">Cancellation Reason:</p>
                              <p className="text-sm text-red-700">{rental.cancellationReason}</p>
                              {rental.cancelledBy && (
                                <p className="text-xs text-red-600 mt-1">
                                  Cancelled by: {rental.cancelledBy}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          {rental.status === 'confirmed' && (
                            <>
                              {cancellationCheck.allowed ? (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleCancel(rental._id)}
                                >
                                  Cancel
                                </Button>
                              ) : (
                                <div className="text-sm text-gray-500 text-right max-w-xs">
                                  <p className="font-medium text-gray-700 mb-1">Cannot cancel</p>
                                  <p>{cancellationCheck.reason}</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyRentalsPage;

